import './price-decision-tooltip.scss';
import { CustomTooltip } from '@/components/custom-tooltip';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { LoadingBar } from 'components/loading-bar/loading-bar';
import { RecRateExpectation } from 'graphql/gql-types';
import { labels as globalLabels } from 'locales/en.label';
import { FaCheck } from 'react-icons/fa';
import { IoTriangle } from 'react-icons/io5';
import type { Maybe } from 'types/maybe-type';
import { thousandSeparatorRateFormatter } from '../../tableCustomCellRender';

const labels = globalLabels.rec_review.dashboard.price_decision;

interface RecRateExplainer {
	stay_date?: string | null;
	rec_action?: string | null;
	pickup_ratio?: number | null;
	pickup_pace?: RecRateExpectation | null;
	pricing_ratio?: number | null;
	pricing_pace?: RecRateExpectation | null;
	incr_revenue?: number | null;
}

interface PriceDecisionTooltipProps {
	data: Maybe<RecRateExplainer>;
	loading: boolean;
	recRate: Maybe<number>;
	bwhRate: Maybe<number>;
	children: React.ReactElement<any, any> & React.ReactNode;
}

const Value = ({
	pace,
	ratio,
}: {
	pace: Maybe<RecRateExpectation>;
	ratio: Maybe<number>;
}) => {
	if (!pace || !ratio) return <div className='value' />;
	return (
		<div className='value'>
			{ratio.toFixed(2)}
			{pace === RecRateExpectation.INLINE ? (
				<FaCheck className='icon check' />
			) : (
				<IoTriangle
					className={`icon triangle-${
						pace === RecRateExpectation.HIGH ? 'up' : 'down'
					}`}
				/>
			)}
		</div>
	);
};

export const PriceDecisionTooltip = ({
	data,
	loading,
	recRate,
	bwhRate,
	children,
}: PriceDecisionTooltipProps) => {
	return (
		<CustomTooltip
			placement='bottom'
			backgroundColor='tw-bg-white'
			arrowColor='tw-fill-primary'
			arrow
			borderColor='tw-border-primary'
			content={
				<LoadingBar className='price-decision-tooltip' loading={loading}>
					<div className='content'>
						<h1 className='header'>{labels.eval_demand_pattern}</h1>
						<div className='body'>
							<div className='left'>
								<div className='label'>{labels.recommended_action}</div>
								{recRate != null && bwhRate != null && recRate > bwhRate && (
									<>
										<ArrowUpward className='icon arrow-up' />
										<div className='value'>{labels.inc}</div>
									</>
								)}
								{recRate != null && bwhRate != null && recRate < bwhRate && (
									<>
										<ArrowDownward className='icon arrow-down' />
										<div className='value'>{labels.dec}</div>
									</>
								)}
								{recRate != null && bwhRate != null && recRate === bwhRate && (
									<>
										<FaCheck className='icon check' />
										<div className='value'>{labels.nc}</div>
									</>
								)}
							</div>
							<hr className='divider' />
							<div className='right'>
								<div className='list-item'>
									<div className='label'>{labels.price}</div>
									<Value
										pace={data?.pricing_pace}
										ratio={data?.pricing_ratio}
									/>
								</div>
								<div className='list-item'>
									<div className='label'>{labels.pickup}</div>
									<Value pace={data?.pickup_pace} ratio={data?.pickup_ratio} />
								</div>
								<div className='list-item'>
									<div className='label'>Incremental Revenue</div>
									<div className='value'>
										{thousandSeparatorRateFormatter(data?.incr_revenue)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</LoadingBar>
			}
		>
			{children}
		</CustomTooltip>
	);
};

export default PriceDecisionTooltip;
